import React from 'react'
import { InferGetServerSidePropsType } from 'next'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import SearchResultsPage from '@scentregroup/shared/pages/search-results'
import Layout from '@scentregroup/shared/hub-components/layout'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'

import {
  makeClients,
  makeOffsetlessTimeGetters,
  doQueryAlgolia,
  QueryContext,
  SegmentHits,
} from '@scentregroup/shared/components/search'
import { getAlgoliaApiKeys, NATIONAL_CONFIG } from '@scentregroup/shared/config'
import { getDefaultNationalMetaData } from '@scentregroup/shared/helpers/get-default-national-metadata'
import { removeUndefinedProps } from '@scentregroup/shared/helpers/utils'
import { GetServerSideProps, GetServerSidePropsContext } from '../types/next'
import { makeApolloClient } from '../lib/apolloClient'
import {
  getTopStripProps,
  ITopStripProps,
} from '@scentregroup/shared/hub-components/top-strip'
import { getLocalUserFromRequestCookies } from '@scentregroup/shared/hooks/use-local-user'
import {
  fetchNationalStaticData,
  NationalStaticData,
} from '@scentregroup/shared/helpers/fetch-header-static-data'

interface SearchPageProps {
  nationalHeaderData: NationalStaticData
  algoliaResults: SegmentHits
  country: ReturnType<typeof localeToCountry>
  topStripProps: ITopStripProps | null
  canonical: string
}

export const getServerSideProps: GetServerSideProps<SearchPageProps> = async ({
  locale,
  query,
  res,
  req,
}: GetServerSidePropsContext) => {
  setCacheHeaders(res)
  const META_INFORMATION =
    locale === 'en-NZ' ? NATIONAL_CONFIG.nz : NATIONAL_CONFIG.au
  const canonical = `${META_INFORMATION.url}${req.url}`

  const country = localeToCountry(locale)
  const client = makeApolloClient()
  const algoliaClients = makeClients(getAlgoliaApiKeys())
  const searchQuery = Array.isArray(query.q) ? query.q[0] : query.q
  const { getOffsetlessTimeInDefaultTz } = makeOffsetlessTimeGetters(country)
  const user = getLocalUserFromRequestCookies(req.cookies)
  const searchContext: QueryContext = {
    user,
    country,
    getOffsetlessTimeInDefaultTz,
  }

  const [nationalHeaderData, algoliaResults, topStripProps] = await Promise.all(
    [
      fetchNationalStaticData(client, { country }),
      doQueryAlgolia({
        query: searchQuery || '',
        clients: algoliaClients,
        context: searchContext,
      }),
      getTopStripProps(client, country),
    ]
  )

  return {
    props: {
      country,
      topStripProps,
      nationalHeaderData,
      algoliaResults: removeUndefinedProps({
        ...algoliaResults,
        products: undefined,
      }),
      canonical,
    },
  }
}

const NationalSearchResultsPage: React.FC<
  React.PropsWithChildren<
    InferGetServerSidePropsType<typeof getServerSideProps>
  >
> = ({ nationalHeaderData, algoliaResults, country, topStripProps }) => {
  const defaultMetaData = getDefaultNationalMetaData({
    country,
    type: 'search',
  })

  return (
    <Layout
      country={country}
      topStrip={topStripProps}
      centre={undefined}
      header={{
        variant: 'national',
        data: nationalHeaderData,
      }}
      metaData={defaultMetaData}
      showRobotsMetaTag={false}
    >
      <SearchResultsPage algoliaResults={algoliaResults} />
    </Layout>
  )
}

export default NationalSearchResultsPage
